import type { ColumnConfiguration, ListBuilderConfiguration } from '../types';

/**
 * PD-17505 — a customer's Item Numbers tab: their own numbers for our items,
 * with their description, our item and code, how each number was recorded and
 * when. Rows are `client_item` rows with a number, from
 * GET /api/clients/:clientId/item-numbers, paged, searched and sorted on the
 * server (a large customer can have thousands).
 */
const TABLE = 'client_item';
const MANAGE = 'clients:item_numbers:manage';

const textColumn = (
	id: string,
	header: string,
	fieldName: string,
	options: { weight?: 'medium'; preset?: 'small' | 'medium' | 'large' } = {},
): ColumnConfiguration => ({
	id,
	fields: [
		{
			id: `${id}-value`,
			dataConfig: {
				type: 'field',
				field: { fieldName, objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'md',
				...(options.weight != null ? { weight: options.weight } : {}),
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: `h-${id}`, text: header }],
	minWidth: 140,
	sortable: true,
	sortFields: [{ objectTable: TABLE, fieldName, direction: 'ASC' }],
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: options.preset ?? 'medium' },
	verticalAlignment: 'top',
});

export const CLIENT_ITEM_NUMBERS_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-item-numbers',
			cacheTTL: 0,
			name: 'Item Numbers',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'column-number-their-number-asc',
			columns: [
				textColumn('number-their-number', 'Their Number', 'clientItemNumber', {
					weight: 'medium',
					preset: 'small',
				}),
				textColumn('number-their-description', 'Their Description', 'clientItemDescription'),
				{
					id: 'number-our-item',
					fields: [
						{
							id: 'number-our-item-description',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item',
								idField: 'itemId',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemDescription', objectTable: TABLE },
								defaultValue: '-',
							},
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
						{
							id: 'number-our-item-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
							conditionalVisibility: { field: 'itemCode', condition: 'is_not_null' },
						},
						{
							id: 'number-our-item-code',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemCode', objectTable: TABLE },
							},
							styleConfig: { size: 'xs', colorRule: { type: 'muted' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'itemCode', condition: 'is_not_null' },
						},
					],
					headers: [{ id: 'h-number-our-item', text: 'Our Item' }],
					minWidth: 200,
					sortable: true,
					sortFields: [{ objectTable: TABLE, fieldName: 'itemDescription', direction: 'ASC' }],
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'number-recorded-how',
					fields: [
						{
							id: 'number-recorded-how-value',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'recordedHow', objectTable: TABLE },
							},
							styleConfig: { size: 'md', colorRule: { type: 'muted' } },
							displayStyle: 'text',
						},
						{
							id: 'number-recorded-how-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
							conditionalVisibility: { field: 'quoteRequestId', condition: 'is_not_null' },
						},
						{
							id: 'number-recorded-how-request',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'cross_reference_request',
								idField: 'quoteRequestId',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quoteRequestLabel', objectTable: TABLE },
							},
							styleConfig: { size: 'xs', colorRule: { type: 'default' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'quoteRequestId', condition: 'is_not_null' },
						},
					],
					headers: [{ id: 'h-number-recorded-how', text: 'How It Was Recorded' }],
					minWidth: 180,
					sortable: true,
					sortFields: [{ objectTable: TABLE, fieldName: 'recordedHow', direction: 'ASC' }],
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'number-recorded',
					fields: [
						{
							id: 'number-recorded-at',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'recordedAt', objectTable: TABLE },
								defaultValue: '-',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'short' },
							},
							displayStyle: 'date',
						},
						{
							id: 'number-recorded-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
							conditionalVisibility: { field: 'recordedByName', condition: 'is_not_null' },
						},
						{
							id: 'number-recorded-by',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'recordedByName', objectTable: TABLE },
							},
							styleConfig: { size: 'xs', colorRule: { type: 'muted' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'recordedByName', condition: 'is_not_null' },
						},
					],
					headers: [{ id: 'h-number-recorded', text: 'Recorded' }],
					minWidth: 120,
					sortable: true,
					sortFields: [{ objectTable: TABLE, fieldName: 'recordedAt', direction: 'DESC' }],
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'number-actions',
					fields: [
						{
							id: 'number-actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'number-edit',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editNumber',
									requiredPermission: MANAGE,
								},
								{
									id: 'number-remove',
									label: 'Remove',
									iconName: 'trash',
									callbackName: 'removeNumber',
									variant: 'danger',
									requiredPermission: MANAGE,
								},
							],
						},
					],
					headers: [{ id: 'h-number-actions', text: '' }],
					width: 80,
					sortable: false,
					requiredPermission: MANAGE,
				},
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['clientItemNumberKeywords'],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Client Item Number',
			objectTable: TABLE,
			// Rows are client_item rows keyed by `id`.
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: 'clientItemNumber', fieldLabel: 'Their Number' },
				{ fieldName: 'clientItemDescription', fieldLabel: 'Their Description' },
				{ fieldName: 'itemDescription', fieldLabel: 'Our Item' },
				{ fieldName: 'itemCode', fieldLabel: 'Our Item Code' },
				{ fieldName: 'recordedHow', fieldLabel: 'How It Was Recorded' },
				{ fieldName: 'recordedAt', fieldLabel: 'Recorded' },
			],
		},
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [{ fieldName: 'itemId', fieldLabel: 'ID' }],
		},
		{
			baseUrl: '/quote-requests/:id',
			objectLabel: 'Quote Request',
			objectTable: 'cross_reference_request',
			selectedFields: [{ fieldName: 'quoteRequestId', fieldLabel: 'ID' }],
		},
	],
};
